import React, { PureComponent } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Spotlight } from '@atlaskit/onboarding';

import { getCurrentRepositoryOwner } from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';
import {
  publishFact,
  publishUiEvent,
  publishScreenEvent,
} from 'src/utils/analytics/publish';

import DiscoveryCardContent from './discovery-card-content';
import messages from './discovery-card.i18n';
import {
  DiscoveryCardView,
  DiscoveryCardMinimize,
  DiscoveryCardExpand,
  DiscoveryCardClosed,
} from './facts';
import { shouldDisplayCard, dismissCard } from './store';

type DiscoveryCardProps = {
  intl: InjectedIntl;
  isCollapsed?: boolean;
  isOwner: boolean;
  isCardEnabled?: boolean;
  userUuid?: string;
};

type DiscoveryCardState = {
  isSpotlightActive: boolean;
  isCardDismissed?: boolean;
};

class BaseDiscoveryCard extends PureComponent<
  DiscoveryCardProps,
  DiscoveryCardState
> {
  state: DiscoveryCardState = {
    isSpotlightActive: false,
    isCardDismissed: false,
  };

  componentDidMount() {
    const { isOwner, isCollapsed, userUuid } = this.props;
    if (!isCollapsed && isOwner && userUuid && shouldDisplayCard(userUuid)) {
      // @ts-ignore
      publishFact(new DiscoveryCardView());
      publishScreenEvent('discovery-card-view');
    }
  }

  handleClose = () => {
    const { userUuid } = this.props;
    if (userUuid) {
      dismissCard(userUuid);
    }
    this.setState({ isSpotlightActive: true, isCardDismissed: true });
    // @ts-ignore
    publishFact(new DiscoveryCardClosed());
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'discoveryCardCloseButton',
      source: 'discoveryCard',
    });
  };

  handleDismiss = () => {
    this.setState({ isSpotlightActive: false, isCardDismissed: true });
  };

  toggleCollapse = (isCollapsed: boolean) => {
    if (!isCollapsed) {
      // @ts-ignore
      publishFact(new DiscoveryCardMinimize());
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'discoveryCardMinimizeButton',
        source: 'discoveryCard',
      });
    } else {
      // @ts-ignore
      publishFact(new DiscoveryCardExpand());
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'discoveryCardExpandButton',
        source: 'discoveryCard',
      });
    }
  };

  render() {
    const { isSpotlightActive, isCardDismissed } = this.state;
    const { intl, isCardEnabled, isCollapsed, isOwner, userUuid } = this.props;

    const inDismissalFlow = isSpotlightActive && isCardDismissed;

    if (!isCardEnabled) {
      return null;
    }

    if (isOwner) {
      if (inDismissalFlow) {
        return (
          <Spotlight
            actions={[
              {
                onClick: () => this.handleDismiss(),
                text: intl.formatMessage(messages.ok),
              },
            ]}
            dialogPlacement="left bottom"
            dialogWidth={240}
            key="navigation-account-menu"
            target="navigation-account-menu"
            targetRadius={24}
          >
            <p>
              {intl.formatMessage(messages.spotlightMessage)}
              <strong>{intl.formatMessage(messages.integrations)}</strong>.
            </p>
          </Spotlight>
        );
      }

      if (!isCardDismissed && userUuid && shouldDisplayCard(userUuid)) {
        return (
          <DiscoveryCardContent
            isCollapsed={isCollapsed}
            handleClose={this.handleClose}
            toggleCollapse={this.toggleCollapse}
          />
        );
      }
    }
    return null;
  }
}

export const BaseDiscoveryCardWithIntl = injectIntl(BaseDiscoveryCard);

const mapStateToProps = (state: BucketState) => {
  const user = getCurrentUser(state);
  const repositoryOwner = getCurrentRepositoryOwner(state);

  return {
    isOwner:
      user && repositoryOwner ? user.uuid === repositoryOwner.uuid : false,
    isCardEnabled: state.global.features['discovery-card'],
    userUuid: user ? user.uuid : undefined,
  };
};

export default connect(mapStateToProps)(BaseDiscoveryCardWithIntl);
